/**
 * Artifact service — generate a downloadable artifact (deck/summary/diagram) from a
 * conversation and download a stored one. The export posts an artifact card into the
 * chat (it streams in via the message subscription); download fetches the bytes.
 */
import { callFunction } from '../utils/apiClient';

export type ArtifactKind = 'deck' | 'summary' | 'diagram';

export interface StoredArtifact {
  id: string;
  kind: ArtifactKind;
  mimeType: string;
  filename: string;
}

export const exportConversationArtifact = (conversationId: string, kind: ArtifactKind, format?: 'md' | 'txt' | 'html') =>
  callFunction<{ artifact: StoredArtifact }>('exportConversationArtifact', { conversationId, kind, format });

export const getArtifact = (artifactId: string) =>
  callFunction<{ filename: string; mimeType: string; base64: string }>('getArtifact', { artifactId });

// ── Decision brief (analyst-workflow Synthesize step) — a live, structured multi-lens synthesis of the chat ──
export interface BriefItem { text: string; by?: string }
/** A flexible brief section — the model chooses the heading that fits the conversation (not a fixed set). */
export interface BriefSection { heading: string; items: BriefItem[] }
export interface DecisionBrief { thesis?: string; sections: BriefSection[] }
/** On-demand + metered: distil the chat into the sections that fit it, each item attributed to a lens. */
export const summarizeDecisions = (conversationId: string) =>
  callFunction<{ brief: DecisionBrief; messageCount: number }>('summarizeDecisions', { conversationId });

/** Fetch a stored artifact and trigger a browser download. */
export async function downloadArtifact(artifactId: string): Promise<void> {
  const { filename, mimeType, base64 } = await getArtifact(artifactId);
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mimeType }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
